import React, { useEffect, useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { format, parseISO, subDays, startOfToday } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { Trash2, CalendarX2, Ban } from "lucide-react";

export const StayCalendarManager = ({ properties = [] }) => {
  const stays = properties.filter((p) => p.short_term);
  const [pid, setPid] = useState("");
  const [ranges, setRanges] = useState([]);
  const [sel, setSel] = useState(undefined);
  const [reason, setReason] = useState("Indisponible");

  useEffect(() => { if (!pid && stays.length) setPid(stays[0].id); /* eslint-disable-next-line */ }, [properties]);

  const load = async (id) => {
    if (!id) return;
    try { const { data } = await api.get(`/properties/${id}/availability`); setRanges(data || []); } catch {}
  };
  useEffect(() => { load(pid); }, [pid]);

  const disabled = useMemo(() => {
    const list = [{ before: startOfToday() }];
    for (const r of ranges) {
      try { list.push({ from: parseISO(r.check_in), to: subDays(parseISO(r.check_out), 1) }); } catch {}
    }
    return list;
  }, [ranges]);

  const blocks = ranges.filter((r) => r.kind === "blocked");

  const block = async () => {
    if (!pid) return toast.error("Choisissez un logement");
    if (!sel?.from || !sel?.to) return toast.error("Sélectionnez une plage de dates");
    try {
      await api.post(`/properties/${pid}/block`, {
        check_in: format(sel.from, "yyyy-MM-dd"), check_out: format(sel.to, "yyyy-MM-dd"), reason,
      });
      toast.success("Dates bloquées");
      setSel(undefined); load(pid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unblock = async (bid) => { await api.delete(`/properties/${pid}/block/${bid}`); toast.success("Débloqué"); load(pid); };

  if (stays.length === 0) return null;

  return (
    <div className="rounded-xl bg-white border border-black/5 p-5" data-testid="stay-calendar-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-serif text-lg font-semibold text-navy flex items-center gap-2"><CalendarX2 className="h-5 w-5 text-gold" /> Calendrier & disponibilités</h3>
        <div className="w-full sm:w-72">
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="cal-property-select"><SelectValue placeholder="Choisir un logement" /></SelectTrigger>
            <SelectContent>{stays.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4 grid gap-6 md:grid-cols-2">
        <div>
          <p className="mb-2 text-sm text-slate-500">Sélectionnez une plage à bloquer (les dates réservées sont désactivées).</p>
          <div className="rounded-lg border border-black/10 p-2 inline-block">
            <Calendar mode="range" locale={fr} numberOfMonths={1} selected={sel} onSelect={setSel} disabled={disabled} data-testid="cal-range" />
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]"><Label className="text-xs text-slate-500">Motif</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" data-testid="cal-reason" /></div>
            <Button onClick={block} className="bg-navy text-white rounded-lg" data-testid="cal-block-btn"><Ban className="h-4 w-4 mr-1.5" /> Bloquer ces dates</Button>
          </div>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-navy">Périodes bloquées manuellement</p>
          {blocks.length === 0 ? <p className="text-sm text-slate-400">Aucune période bloquée.</p> : (
            <div className="space-y-2">
              {blocks.map((b) => (
                <div key={b.id} className="flex items-center justify-between rounded-lg bg-ivory px-3 py-2 text-sm" data-testid={`block-${b.id}`}>
                  <span>{b.check_in} → {b.check_out} <span className="text-slate-400">· {b.reason}</span></span>
                  <button onClick={() => unblock(b.id)} className="text-red-500 hover:text-red-700" data-testid={`unblock-${b.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
          <p className="mt-4 text-xs text-slate-400">Les réservations actives apparaissent aussi comme indisponibles dans le calendrier public.</p>
        </div>
      </div>
    </div>
  );
};
